import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Insignia, Tarjeta } from '../componentes/ui/basicos';
import { Cargando, Problema, Vacio } from '../componentes/ui/estados';
import { bajarEnLatin1 } from '../lib/planilla';

interface ArchivoDelLibro {
  readonly nombre: string;
  readonly contenido: string;
}

interface LibroDeIvaDigital {
  readonly periodo: string;
  readonly archivos: readonly ArchivoDelLibro[];
  readonly resumen: {
    readonly ventas: number;
    readonly alicuotasDeVentas: number;
    readonly compras: number;
    readonly alicuotasDeCompras: number;
    readonly anulados: number;
  };
}

/** El mes anterior: es el que se presenta, porque el actual todavía no cerró. */
function mesAnterior(): string {
  const ahora = new Date();
  const mes = new Date(Date.UTC(ahora.getUTCFullYear(), ahora.getUTCMonth() - 1, 1));
  return mes.toISOString().slice(0, 7);
}

function renglonesDe(archivo: ArchivoDelLibro): number {
  return archivo.contenido === '' ? 0 : archivo.contenido.trimEnd().split('\r\n').length;
}

/**
 * El Libro de IVA Digital.
 *
 * Lo que esta pantalla tiene que hacer bien no es verse linda: es **que el archivo que se baja se
 * pueda importar**. Tres cosas lo deciden y las tres están acá:
 *
 * - **El período es un mes** y arranca en el anterior, que es el que se presenta.
 * - **Cada archivo se baja por separado y con su nombre exacto.** El importador del organismo
 *   elige el diseño de registro por el nombre: uno bien armado con el nombre cambiado se rechaza
 *   igual, y por eso el nombre no se puede editar desde acá.
 * - **La descarga va en ISO 8859-1.** Es lo que el régimen exige y lo que rompe todo si se pasa
 *   por alto: en un archivo de ancho fijo, una "ñ" en UTF-8 corre todas las posiciones desde ese
 *   renglón hasta el final.
 *
 * Y se muestran los primeros renglones de cada archivo. No es para lucirse: es para que quien lo
 * presenta pueda mirar antes de subir, que es la única revisión posible cuando el rechazo del otro
 * lado no dice dónde está el problema.
 */
export function LibroDeIva(): React.ReactElement {
  const { t } = useTranslation();
  const [periodo, setPeriodo] = useState(mesAnterior);
  const [pedido, setPedido] = useState<string | null>(null);

  const consulta = useQuery({
    queryKey: ['libro-de-iva', pedido],
    enabled: pedido !== null,
    queryFn: () => pedir<LibroDeIvaDigital>(`/libro-de-iva?periodo=${pedido ?? ''}`),
  });

  const libro = consulta.data;
  const total =
    libro === undefined ? 0 : libro.resumen.ventas + libro.resumen.compras + libro.resumen.anulados;

  return (
    <section className="flex flex-col gap-4">
      <Encabezado titulo={t('libroDeIva.titulo')} descripcion={t('libroDeIva.descripcion')} />

      <Tarjeta>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-texto-tenue">
            {t('libroDeIva.periodo')}
            <input
              type="month"
              value={periodo}
              onChange={(evento) => {
                setPeriodo(evento.target.value);
              }}
              className="rounded-marco border border-borde bg-superficie px-2 py-1 text-sm text-texto"
            />
          </label>
          <Boton
            tono="principal"
            onClick={() => {
              setPedido(periodo);
            }}
          >
            {t('libroDeIva.generar')}
          </Boton>
        </div>

        {/* La advertencia va en la pantalla y no en un manual: es la que evita el rechazo. */}
        <p className="mt-3 text-xs text-texto-tenue">{t('libroDeIva.avisoDeCodificacion')}</p>
      </Tarjeta>

      {consulta.isError ? <Problema error={consulta.error} /> : null}
      {pedido !== null && consulta.isPending ? <Cargando /> : null}

      {libro === undefined ? null : (
        <Tarjeta>
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold">
            {libro.periodo}
            <Insignia tono={total === 0 ? 'atencion' : 'bien'}>
              {total === 0 ? t('libroDeIva.sinMovimientos') : t('libroDeIva.registros', { total })}
            </Insignia>
          </h2>

          <div className="flex flex-col gap-3">
            {libro.archivos.map((archivo) => {
              const renglones = renglonesDe(archivo);
              return (
                <div
                  key={archivo.nombre}
                  className="flex items-start justify-between gap-3 border-t border-borde pt-3"
                >
                  <div className="min-w-0">
                    <p className="numero text-xs">{archivo.nombre}</p>
                    <p className="text-xs text-texto-tenue">
                      {t('libroDeIva.renglones', { renglones })}
                    </p>
                    {/* Las primeras líneas, tal cual salen. Un archivo de ancho fijo no se puede
                        revisar de otra manera. */}
                    {renglones === 0 ? null : (
                      <pre className="mt-1 max-w-full overflow-x-auto rounded-marco bg-acento-suave p-2 text-[10px] leading-tight">
                        {archivo.contenido.trimEnd().split('\r\n').slice(0, 3).join('\n')}
                      </pre>
                    )}
                  </div>
                  <Boton
                    disabled={renglones === 0}
                    onClick={() => {
                      bajarEnLatin1(archivo.nombre, archivo.contenido);
                    }}
                  >
                    {t('libroDeIva.bajar')}
                  </Boton>
                </div>
              );
            })}
          </div>
        </Tarjeta>
      )}

      {pedido === null ? <Vacio titulo={t('libroDeIva.todaviaNada')} /> : null}
    </section>
  );
}
